// --------------------------------------------------------------
// Created On: 2026-03-23
// Author: Dimitra Weinstein
//
// Last Modified: 2026-05-06
// Modified By: Hannah Vaughan
//
// Copyright 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import React, { Fragment, useState } from "react";
import { useNavigate } from "react-router";
import { stepIntoCompany } from "src/redux/features/currentUserSlice";
import { CROSS_SUBSIDIARY_PERMISSION, RELEASE_SUBSIDIARY_PERMISSION } from "src/constants/permissions";
import userHasPermission from "src/utilities/userHasPermission";
import useReduxSelector from "src/hooks/useReduxSelector";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import Modal from "src/components/Modal/Modal";
import ModalBody from "src/components/ModalBody/ModalBody";
import ModalHeader from "src/components/ModalHeader/ModalHeader";
import ModalFooter from "src/components/ModalFooter/ModalFooter";
import DoubleConfirmModal from "src/components/DoubleConfirmModal/DoubleConfirmModal";
import styles from "./ManageSubsidiaryModal.module.scss";
import apiRequest from "src/utilities/api/apiRequest";
import { API } from "src/constants/miscellaneous";
import getApiError from "src/utilities/api/getApiError";
import ErrorBanner from "src/components/ErrorBanner/ErrorBanner";

// Modal that allows users to manage a subsidiary company. Users can step into the subsidiary company or release the subsidiary company.
export default function ManageSubsidiaryModal(props: Props): Component {
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [showReleaseCompanyModal, setShowReleaseCompanyModal] = useState<boolean>(false);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const dispatch = useReduxDispatch();
  const navigate = useNavigate();

  // Enter into the subsidiary company, temporarily updating the users permissions to those that an admin would have.
  async function stepIntoSubsidiary(subsidiaryCompanyId: number, subsidiaryCompanyName: string): Promise<void> {
    const [response, responseBody] = (await apiRequest(`${API}/admin-permissions`, "GET", null)) as [
      Response,
      AdminPermissionsResponseBody
    ];
    if (response.ok) {
      const adminPermissions: string[] = responseBody.permissions.map((permission) => permission.code);
      dispatch(
        stepIntoCompany({
          companyId: subsidiaryCompanyId,
          companyName: subsidiaryCompanyName,
          isPackager: currentUser.isPackager,
          permissions: adminPermissions,
        })
      );
      navigate("/dashboard/map", { replace: true });
    } else {
      setErrorMessage(
        await getApiError(response, "Unable to retrieve user company permissions needed to step into subsidiary.")
      );
    }
  }

  // Close the Release Company Modal, then continue with releasing the subsidiary.
  function releaseSubsidiary(subsidiaryCompanyId: number): void {
    setShowReleaseCompanyModal(false);
    props.onReleaseCompany(subsidiaryCompanyId);
  }

  return (
    <Fragment>
      <Modal
        show={props.showModal}
        onHide={() => props.onClose()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="xl"
        centered
        animation
      >
        <ModalHeader>
          <h5 className="font-weight-bold">Manage Subsidiary Company</h5>
        </ModalHeader>
        <ModalBody>
          <div className="row px-4">
            <h3 className="mb-3 px-2 mx-auto fs-5 fw-medium">{props.subsidiaryCompanyName}</h3>
            <div className="col-12 px-4 align-items-center">
              <div className="row px-4 py-2 mb-3 justify-content-center align-items-center bg-primary bg-opacity-25 rounded">
                <p className="col-7 col-xl-8 px-4 pt-2 fw-medium">
                  Enter into the subsidiary to create and manage its resources.
                </p>
                <div className="col-5 col-xl-4 px-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    disabled={!userHasPermission([[CROSS_SUBSIDIARY_PERMISSION]])}
                    onClick={() => stepIntoSubsidiary(props.subsidiaryCompanyId, props.subsidiaryCompanyName)}
                  >
                    Step Into Subsidiary
                  </button>
                </div>
              </div>

              <div className="row px-4 py-2 mb-3 justify-content-center align-items-center bg-primary bg-opacity-25 rounded">
                <p className="col-7 col-xl-8 px-4 pt-2 fw-medium">
                  Release the subsidiary. This action discontinues association between your company and the subsidiary.
                </p>
                <div className="col-5 col-xl-4 px-4">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowReleaseCompanyModal(true)}
                    disabled={!userHasPermission([[RELEASE_SUBSIDIARY_PERMISSION]])}
                  >
                    Release Subsidiary
                  </button>
                </div>
              </div>
              {errorMessage.length > 0 && <ErrorBanner message={errorMessage} />}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="row">
            <div className="col-auto px-auto">
              <button type="button" className="btn btn-primary" onClick={() => props.onClose()}>
                Cancel
              </button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <DoubleConfirmModal
        showModal={showReleaseCompanyModal}
        title="Confirm Release of Subsidiary Company"
        content="Are you sure you want to release this subsidiary company? Once released, you will no longer be able to access the company or manage its resources. The company will also be open to rental requests from other companies. Any assets you have rented to this company will remain rented."
        confirmPin="Release Company"
        confirmText="Release Company"
        errorMessage={props.errorMessage}
        onConfirm={() => releaseSubsidiary(props.subsidiaryCompanyId)}
        onCancel={() => setShowReleaseCompanyModal(false)}
      />
    </Fragment>
  );
}

interface Props {
  showModal: boolean;
  subsidiaryCompanyId: number;
  subsidiaryCompanyName: string;
  errorMessage: string;
  onReleaseCompany: (subsidiaryCompanyId: number) => void;
  onClose: () => void;
}

interface CompanyPermission {
  permissionId: number;
  code: string;
}

interface AdminPermissionsResponseBody {
  permissions: CompanyPermission[];
}
